import { Inject, Injectable, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { Subject, of, BehaviorSubject } from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {BreakpointObserver} from '@angular/cdk/layout';
import { GenericConfig } from '@signature-it/ngx-generic/config';
import { EventData, EventName } from '@signature-it/ngx-generic/classes';
import { EventBusService } from './event-bus.service';

declare let _ : any;

@Injectable({
    providedIn: 'root'
})
export class ShareDataService implements OnDestroy {
    solrContextSubject = new BehaviorSubject(false);
    loginData$ = new BehaviorSubject(null);
    showBreadcrumbs$ = new Subject<boolean>();
    showCatalogueBreadcrumbs$ = new Subject<boolean>();
    isRestrictedLogin$ = new BehaviorSubject(null);
    mobileBreakPoint = GenericConfig.mobileBreakPoint ? GenericConfig.mobileBreakPoint : 992;
    gridBreakPoints = GenericConfig.gridBreakPoints ? GenericConfig.gridBreakPoints : {xs: 0, sm: 576, md: 768, lg: 992, xl: 1200};
    isMobileMode$;
    viewPortWidth = window.innerWidth;
    bpRanges = new Map();
    currentScreenSize: string;
    destroyed = new Subject<void>();
    gridRangesArray;

    constructor(@Inject(PLATFORM_ID) private platformId: string, private eventBusService: EventBusService, private bpObserver: BreakpointObserver) {
        this.isMobileMode$ = new BehaviorSubject(this.isMobileMode());
        window.addEventListener('resize', () => {
            this.isMobileMode$.next(this.isMobileMode());
            this.viewPortWidth = window.innerWidth;
        });


        this.gridRangesArray = this.setupBpsData();
        bpObserver.observe(this.gridRangesArray)
            .pipe(takeUntil(this.destroyed))
            .subscribe(result => {
                for (const query of Object.keys(result.breakpoints)){
                    if (result.breakpoints[query]) {
                        this.currentScreenSize = this.bpRanges.get(query) ?? 'Unknown';
                    }
                }
            });
    }

    private setupBpsData(specifiedBPs?: Array<string>){
        let gridArray;
        if(specifiedBPs){
            gridArray = Object.entries(this.gridBreakPoints).filter(i => specifiedBPs.includes(i[0]))
        } else {
            gridArray = Object.entries(this.gridBreakPoints);
        }
        gridArray.forEach((value, index, array) => {
            if(index === array.length - 1){
                this.bpRanges.set('(min-width: '+value[1]+'px)', value[0]);
            } else {
                this.bpRanges.set('(min-width: '+value[1]+'px) and (max-width: '+(+array[index+1][1] - 0.02)+'px)', value[0]);
            }
        });
        return [...this.bpRanges.keys()];
    }


    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }


    /** Initialize isLogin */
    public isLogin(isLogin) {
        return of(isLogin)
            .subscribe((value: any) => this.loginData$.next(value));
    }

    /** Initialize isRestricted */
    public isRestricted(isRestricted) {
        return of(isRestricted)
            .subscribe((value: any) => this.isRestrictedLogin$.next(value));
    }


    public isMobileMode(): boolean {
        return this.viewPortWidth < this.mobileBreakPoint;
        // return document.documentElement.clientWidth	<= 991;
    }

    /** is view port bigger than ... */
    public isBpUp(bp: string) {
        return  this.viewPortWidth >= this.gridBreakPoints[bp];
    }

    /** get current break point from config's object */
    public getCurrentBp(relevantKeys: string[]){

        const relevantBps = _.pick(this.gridBreakPoints, relevantKeys);

        // arrange breakpoints list in a descending order
        const sorted = _.entries(relevantBps).sort((a, b) => <number>b[1]-<number>a[1]);

        // and return the first (and highest) item that is smaller than the current viewport width
        for (let [key, value] of sorted){
            if(value <= this.viewPortWidth){
                return key;
                break;
            }
        }
    }

    /** check if config bp matches the current viewport width. Note - this method responds to screen size changes ONLY inside Angular templates */
    public isBpMatched(bpMatch: string){
        return this.bpObserver.isMatched(String([...this.bpRanges].find(([key, value]) => value === bpMatch)));
    }

    /** check if media query matches the current viewport width. Note - this method responds to screen size changes ONLY inside Angular templates */
    public isMediaQueryMatched(mediaQuery: string){
        return this.bpObserver.isMatched(mediaQuery);
    }


    setFinishedSolrContext(data) {
        return of(data)
            .subscribe((value) => {
                this.solrContextSubject.next(value);
            });
    }

    isBrowser() {
        return isPlatformBrowser(this.platformId);
    }

    isServer() {
        return isPlatformServer(this.platformId);
    }

    public showDependElements(dependSelector) {
        if (!dependSelector) return;
        let elems = Array.from(document.querySelectorAll(dependSelector));
        elems.forEach(el => {
            el.classList.remove('d-none');
        });
        this.eventBusService.emit(new EventData(EventName.HIDE_CMP_DEPEND_EL, false));
    }

    public hideDependElements(dependSelector) {
        if (!dependSelector) return;
        let elems = Array.from(document.querySelectorAll(dependSelector));
        elems.forEach(el => {
            el.classList.add('d-none');
        });
        this.eventBusService.emit(new EventData(EventName.HIDE_CMP_DEPEND_EL, true));
    }

}
